/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import { EnterpriseProgramMemberships_ProgramMembershipState as ProgramMembershipState } from '__generated__/graphql-types-do-not-use';
import { compose, getDisplayName, setDisplayName } from 'recompose';

import { Button } from '@coursera/cds-core';

import EnrollmentChoiceModal from 'bundles/enterprise-legacy-learner-home/components/EnrollmentChoiceModal';
import EnrollmentChoiceModalProductDescription from 'bundles/enterprise-legacy-learner-home/components/EnrollmentChoiceModalProductDescriptionWithProductIdAndType';
import EnrollmentChoiceModalProgramList from 'bundles/enterprise-legacy-learner-home/components/EnrollmentChoiceModalProgramList';
import EnrollmentChoiceModalSingleProgram from 'bundles/enterprise-legacy-learner-home/components/EnrollmentChoiceModalSingleProgram';
import {
  PRODUCT_TYPES,
  PRODUCT_TYPES_SERVER,
} from 'bundles/enterprise-legacy-learner-home/constants/ProgramActionConstants';
import { EnterpriseProgramSessionAssociationsByProgramsAndCourseQuery } from 'bundles/enterprise-legacy-learner-home/constants/ProgramCommonGraphqlQueries';
import type {
  EnterpriseProgramSessionAssociationsByProgramsAndCourseQuery_EnterpriseProgramSessionAssociationsV1Resource_byProgramsAndCourse_elements as EnterpriseProgramSessionAssociation,
  EnterpriseProgramSessionAssociationsByProgramsAndCourseQuery as EnterpriseProgramSessionAssociationsByProgramsAndCourseQueryType,
  EnterpriseProgramSessionAssociationsByProgramsAndCourseQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/constants/__generated__/EnterpriseProgramSessionAssociationsByProgramsAndCourseQuery';
import EnrollmentChoiceModalQuery from 'bundles/enterprise-legacy-learner-home/queries/EnrollmentChoiceModalQuery.graphql';
import ProgramsAndCoursesMetadataQuery from 'bundles/enterprise-legacy-learner-home/queries/ProgramsAndCoursesMetadata.graphql';
import ProgramsAndSpecializationsMetadataQuery from 'bundles/enterprise-legacy-learner-home/queries/ProgramsAndSpecializationsMetadata.graphql';
import type {
  EnrollmentChoiceModalQueryQuery as EnrollmentChoiceModalQueryType,
  EnrollmentChoiceModalQueryQueryVariables as EnrollmentChoiceModalQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/EnrollmentChoiceModalQuery';
import type {
  ProgramsAndCoursesMetadataQuery as ProgramsAndCoursesMetadataQueryData,
  ProgramsAndCoursesMetadataQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/ProgramsAndCoursesMetadata';
import type {
  ProgramsAndSpecializationsMetadataQuery as ProgramsAndSpecializationsMetadataQueryData,
  ProgramsAndSpecializationsMetadataQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/ProgramsAndSpecializationsMetadata';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

type PropsFromCaller = {
  onClose: () => void;
  onSelect: (programId: string) => void;
  productId: string;
  productType: string;
  programIds: string[];
  programJoinTrackingVerbiage: boolean;
  thirdPartyOrgName: string;
  userId: number;
  headerTitle: string;
  buttonTitle: string;
  isEnrolling?: boolean;
  onOptionsReady?: (programId?: string) => void;
};

type ProgramOption = {
  programId: string;
  programName: string;
  date?: Date;
  isInvitation: boolean;
};

type PropsFromGraphql = {
  loading: boolean;
  error: boolean;
  programOptions: Array<ProgramOption>;
};

type PropsFromGraphql2 = {
  programsWithCredits?: Array<string | undefined>;
  programsWithCreditsLoading: boolean;
  programsWithCreditsError: boolean;
};

type PropsFromGraphql3 = {
  enterpriseProgramAssociations?: { [key: string]: EnterpriseProgramSessionAssociation };
  enterpriseProgramAssociationsLoading: boolean;
  enterpriseProgramAssociationsError: boolean;
};

type Props = PropsFromCaller & PropsFromGraphql & PropsFromGraphql2 & PropsFromGraphql3;

const EnrollmentChoiceModalForMultiprogram = ({
  onClose,
  onSelect,
  productId,
  productType,
  programJoinTrackingVerbiage,
  loading,
  programIds,
  programOptions,
  thirdPartyOrgName,
  headerTitle,
  buttonTitle,
  programsWithCredits,
  enterpriseProgramAssociations,
  isEnrolling,
  onOptionsReady,
}: Props) => {
  const [programId, setProgramId] = React.useState<string | undefined>();

  React.useEffect(() => {
    if (programId == null && programOptions.length) {
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      setProgramId(programOptions[0].programId);
    }
  }, [programId, programOptions, setProgramId]);

  const onChangeRadio = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (event: React.ChangeEvent<HTMLInputElement>) => setProgramId(event.target.value),
    [setProgramId]
  );

  const onSelectProgram = React.useCallback<() => $TSFixMe>(() => {
    if (programId) {
      onSelect(programId);
    }
  }, [onSelect, programId]);

  React.useEffect(() => {
    if (!loading && programId != null) {
      onOptionsReady?.(programId);
    }
  }, [loading, programId, onOptionsReady]);

  const programCount = loading ? programIds.length : programOptions.length;
  const isMultiProgram = programCount > 1;

  return (
    <EnrollmentChoiceModal
      headerTitle={headerTitle}
      onClose={onClose}
      button={
        <Button
          variant="primary"
          onClick={onSelectProgram}
          disabled={loading || !programId || isEnrolling}
          aria-live="assertive"
          aria-label={isEnrolling ? _t('Enrolling') : _t('Continue enrolling')}
        >
          {buttonTitle}
        </Button>
      }
    >
      <EnrollmentChoiceModalProductDescription productId={productId} productType={productType} />
      {isMultiProgram && (
        <EnrollmentChoiceModalProgramList
          programId={programId}
          productType={productType}
          programJoinTrackingVerbiage={programJoinTrackingVerbiage}
          thirdPartyOrgName={thirdPartyOrgName}
          programCount={programCount}
          programOptions={programOptions}
          onChangeRadio={onChangeRadio}
          programsWithCredits={programsWithCredits}
          enterpriseProgramAssociations={enterpriseProgramAssociations}
        />
      )}
      {!isMultiProgram && (
        <EnrollmentChoiceModalSingleProgram
          // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          programOption={programOptions[0]}
          productType={productType}
          programJoinTrackingVerbiage={programJoinTrackingVerbiage}
          thirdPartyOrgName={thirdPartyOrgName}
          programsWithCredits={programsWithCredits}
          enterpriseProgramAssociations={enterpriseProgramAssociations}
        />
      )}
    </EnrollmentChoiceModal>
  );
};

export const enhancer = compose<Props, PropsFromCaller>(
  setDisplayName(getDisplayName(EnrollmentChoiceModalForMultiprogram)),
  // TODO(ppaskaris): The XDP metadata query uses fragments that cannot be cached effectively, causing the query to
  // always hit the network. Implement the IntrospectionFragmentMatcher (Frederik has made some progress with this) to
  // fix this issue and be able to serve this query from cache.
  graphql<PropsFromCaller, EnrollmentChoiceModalQueryType, EnrollmentChoiceModalQueryVariables, PropsFromGraphql>(
    EnrollmentChoiceModalQuery,
    {
      options: ({ programIds, userId }) => {
        const programMembershipIds = programIds.map((programId) => ({
          programId,
          userId,
        }));

        return {
          variables: {
            programMembershipIds,
          },
        };
      },
      props: ({ data }) => {
        const programEnrollments = filterExistsOrDefault(
          data?.ProgramMemberships?.queryProgramMemberships?.edges?.map((edge) => edge?.node)
        );
        const programOptions = programEnrollments
          .filter(
            ({ program, createdAt, state }) =>
              program != null &&
              (createdAt != null ||
                state === ProgramMembershipState.ProgramMembershipStateAllowlisted ||
                state === ProgramMembershipState.ProgramMembershipStateInvited)
          )
          .map(({ id, program, createdAt, state }) => ({
            programId: program!.id,
            // See above filter().
            programName: program!.name,
            date: createdAt ? new Date(createdAt) : undefined,
            isInvitation: state === ProgramMembershipState.ProgramMembershipStateInvited,
          }))
          .sort((a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0));
        return {
          loading: data?.loading ?? true,
          error: Boolean(data?.error),
          programOptions,
        };
      },
    }
  ),
  graphql<
    PropsFromCaller,
    ProgramsAndCoursesMetadataQueryData,
    ProgramsAndCoursesMetadataQueryVariables,
    PropsFromGraphql2
  >(ProgramsAndCoursesMetadataQuery, {
    skip: ({ productType }) => productType !== PRODUCT_TYPES.COURSE,
    options: ({ productId, programIds }) => {
      return {
        variables: {
          courseIds: [productId],
          programIds,
        },
      };
    },
    props: ({ data }) => {
      const courses = data?.Course?.queryByIds ?? [];
      const multiProgramMetadata = courses?.[0]?.multiProgramProductMetadata;

      const programsWithCredits = multiProgramMetadata
        ?.filter((metadata) => metadata?.isSelectedForCredit)
        .map((metadata) => metadata?.programId);

      return {
        programsWithCredits,
        programsWithCreditsLoading: data?.loading ?? true,
        programsWithCreditsError: Boolean(data?.error),
      };
    },
  }),
  graphql<
    PropsFromCaller,
    ProgramsAndSpecializationsMetadataQueryData,
    ProgramsAndSpecializationsMetadataQueryVariables,
    PropsFromGraphql2
  >(ProgramsAndSpecializationsMetadataQuery, {
    skip: ({ productType }) => productType !== PRODUCT_TYPES.SPECIALIZATION,
    options: ({ productId, programIds }) => {
      return {
        variables: {
          s12nIds: [productId],
          programIds,
        },
      };
    },
    props: ({ data }) => {
      const specializations = data?.Specialization?.queryByIds ?? [];
      const multiProgramMetadata = specializations?.[0]?.multiProgramProductMetadata;

      const programsWithCredits = multiProgramMetadata
        ?.filter((metadata) => metadata?.isSelectedForCredit)
        .map((metadata) => metadata?.programId);

      return {
        programsWithCredits,
        programsWithCreditsLoading: data?.loading ?? true,
        programsWithCreditsError: Boolean(data?.error),
      };
    },
  }),
  graphql<
    PropsFromCaller,
    EnterpriseProgramSessionAssociationsByProgramsAndCourseQueryType,
    EnterpriseProgramSessionAssociationsByProgramsAndCourseQueryVariables,
    PropsFromGraphql3
  >(EnterpriseProgramSessionAssociationsByProgramsAndCourseQuery, {
    skip: ({ productType }) => productType === PRODUCT_TYPES_SERVER.SPECIALIZATION,
    options: ({ productId, programIds }) => {
      return {
        variables: {
          courseId: productId,
          programIds,
        },
      };
    },
    props: ({ data }) => {
      const enterpriseProgramAssociations =
        data?.EnterpriseProgramSessionAssociationsV1Resource?.byProgramsAndCourse?.elements;
      const enterpriseProgramAssociationsMap = enterpriseProgramAssociations?.reduce(
        (result: { [programId: string]: EnterpriseProgramSessionAssociation }, enterpriseProgramAssociation) => {
          if (enterpriseProgramAssociation?.programId) {
            // eslint-disable-next-line no-param-reassign
            result[enterpriseProgramAssociation.programId] = enterpriseProgramAssociation;
            return result;
          }
          return result;
        },
        {}
      );
      return {
        enterpriseProgramAssociations: enterpriseProgramAssociationsMap,
        enterpriseProgramAssociationsLoading: data?.loading ?? true,
        enterpriseProgramAssociationsError: Boolean(data?.error),
      };
    },
  })
);

export default enhancer(EnrollmentChoiceModalForMultiprogram);
